import {
	type GraphQLSingularResponse,
	type IEnvironment,
	type RequestParameters,
} from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { createRelayEnvironment } from '@atlassian/relay-environment/createRelayEnvironment';
import { type AGGError, captureErrors, isStepUpAuthError } from '@atlassian/relay-errors';
import { SSRQueryResponseCache } from '@atlassian/relay-query-responses';

const getLoggingAndMetricsHelper = () => {
	return {
		logNonBreakingErrors: () => {},
		logError: () => {},
		logErrorRetry: () => {},
		logQueryFallbackToPost: () => {},
	};
};

export const ssrCache: SSRQueryResponseCache = new SSRQueryResponseCache({
	cacheName: 'relay-cache/v2/AGG',
	size: Infinity,
	ttl: Infinity,
});

/**
 * @deprecated use useRelayEnvironment() from react-relay to get this environment instead of using it directly
 */
export const __INTERNAL_RELAY_ENVIRONMENT__DO_NOT_USE_THIS_DIRECTLY__: IEnvironment =
	createRelayEnvironment({
		networkOptions: {
			getLoggingAndMetricsHelper,
			withErrorHandling: (source: Promise<Response>) => source,
			validateResponse: (request: RequestParameters, response: GraphQLSingularResponse) => {
				const errors = 'errors' in response ? response.errors : undefined;
				if (errors != null) {
					const requestId = response?.extensions?.gateway?.request_id;

					captureErrors(errors as AGGError[], { operationName: request.name, requestId });

					if (fg('platform_relay_redirect_on_step_up_auth_studio')) {
						const { hasStepUpAuthError, stepUpUrl } = isStepUpAuthError(errors as AGGError[]);
						// Guard against SSR: window is not defined in server-side rendering environments.
						// RelayEnvironmentProvider is used by platform components (e.g. GlobalSideNavigation)
						// during SSR — validateResponse may be called during renderToString where window
						// does not exist. Without this guard, a step-up auth error would throw
						// ReferenceError and crash the entire SSR render.
						if (hasStepUpAuthError && stepUpUrl && typeof window !== 'undefined') {
							window.location.href = stepUpUrl;
						}
					}
				}
				return true;
			},
			onInvalidResponse: () => {},
			queryResponseCache: ssrCache,
			// Use typeof window === 'undefined' for SSR detection rather than globalThis.__SERVER__.
			// __SERVER__ is set in tests but not in Bifrost's actual SSR environment (it's set in
			// init() which runs after module evaluation). typeof window === 'undefined' is the
			// standard pattern and correctly identifies both Bifrost SSR and test environments.
			isServer: typeof window === 'undefined',
			// In local pillar development, AGG requests need to go through the pillar proxy.
			// We mirror the logic from @atlassian/pillar/runtime/fetch getLocalUrl() to respect
			// custom port (__PILLAR_PROXY_SERVER_PORT) and HTTPS (__PILLAR_ENABLE_HTTPS) settings,
			// keeping routing consistent with the rest of the pillar infrastructure.
			...(process.env.PILLAR_LOCAL_DEVELOPMENT === 'true'
				? {
						setEndpoint: (endpoint: string) => {
							const port = process.env.__PILLAR_PROXY_SERVER_PORT ?? '9000';
							const protocol = process.env.__PILLAR_ENABLE_HTTPS === 'true' ? 'https' : 'http';
							return `${protocol}://localhost:${port}${endpoint}`;
						},
					}
				: {}),
		},
	});
